import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * «No te están oyendo».
 *
 * 🔴 Es el aviso más importante de la pantalla, y hasta hoy no existía.
 *
 * El 23/09/2026 el SDK pasó una llamada entera emitiendo `31005 LOW_LOCAL_AUDIO`
 * cada seis segundos —«Low local microphone audio detected»— mientras la
 * pantalla decía «En conversación» con toda normalidad. El deudor no oyó nada y
 * no hubo forma de saberlo sin abrir la consola del navegador.
 *
 * Un gestor puede pasarse la jornada hablándole a nadie. Y lo peor no es la
 * jornada perdida: esas llamadas entran en los KPIs como **conversaciones
 * útiles** si duran más de 45 s, así que el canal se estaría midiendo a sí mismo
 * como si funcionara.
 *
 * Por eso es rojo y ocupa sitio. Un aviso discreto aquí no sirve de nada.
 *
 * ## 🔴 Por qué el consejo cambió el 15/10/2026
 *
 * Decía «revisa que el micrófono correcto esté seleccionado», y eso **mandaba al
 * gestor justo al fallo**: para comprobarlo se abre la página de ajustes de
 * micrófono de Chrome, cuya **barra de nivel es ella misma una captura del
 * dispositivo** — exactamente lo que deja muda la llamada.
 *
 * Porque la causa casi nunca es el micrófono elegido. En Windows, una pista muda
 * es una **segunda captura del mismo dispositivo**: otra app, otra pestaña, o esa
 * página de ajustes. Ya se documentó el 24/09/2026 en `asegurarMicrofono`, al
 * probar a dejar abierta la pista del permiso. Y pasó de verdad el 15/10: cuatro
 * de ocho llamadas con silencio digital en el canal del gestor —energía 1e-06,
 * ni ruido de fondo— después de 246 llamadas seguidas sin un solo caso.
 *
 * Así que el consejo es **cerrar**, no elegir.
 */

/**
 * Qué hacer, según quién lo detectó y por qué.
 *
 * `LOW_LOCAL_AUDIO` / `LOW_BYTES_SENT` los manda el SDK de Telnyx; los otros tres
 * los medimos nosotros en `vigilanteDeMicrofono`, que sí distingue «no habla» de
 * «no hay micrófono».
 */
const QUE_HACER = {
  sin_voz:
    'Otra aplicación o pestaña tiene cogido el micrófono. Cierra Meet, WhatsApp Web, Teams y la página de ajustes de micrófono de Chrome —su barra de nivel también lo captura—, y vuelve a abrir la sesión.',
  nada_sale:
    'No sale nada de audio de este navegador. Cierra la sesión de llamadas y vuelve a abrirla.',
  sin_pista:
    'El navegador no llegó a capturar el micrófono. Cierra la sesión de llamadas y vuelve a abrirla.',
};

const POR_DEFECTO =
  'El deudor probablemente no te oye. Lo más habitual es que otra aplicación o pestaña tenga cogido el micrófono: ciérralas y vuelve a abrir la sesión.';

const MediaWarning = ({ warning, compact = false }) => {
  if (!warning) return null;

  return (
    <div
      role='alert'
      className={`flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 text-red-800 ${
        compact ? 'px-3 py-1.5 text-sm' : 'p-3'
      }`}
    >
      <ExclamationTriangleIcon className='mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
      <div>
        <p className='font-medium'>No se detecta tu voz</p>
        {!compact && (
          <p className='mt-0.5 text-sm'>{QUE_HACER[warning.name] || POR_DEFECTO}</p>
        )}
      </div>
    </div>
  );
};

export default MediaWarning;
